import {formatEth, type Candle, type Trade} from '@colosseum/shared';
import {html, raw} from './dom.js';

/**
 * A price chart drawn as inline SVG from the candles the indexer folds out of
 * both venues, so a token's history does not break at graduation.
 *
 * Buckets with no trade are not emitted by the API. Carrying the previous close
 * across them is the honest reading: no trade is not a price of zero, and
 * drawing the gap as a fall to the axis would invent a crash.
 */
/** Each trade as its own point, for a token too young to fill two buckets. */
function tradesAsSeries(trades: Trade[]): Candle[] {
  return [...trades]
    .reverse()
    .map((t): Candle => {
      const tokens = BigInt(t.tokens);
      const p = (tokens === 0n ? 0n : (BigInt(t.ethWei) * 10n ** 18n) / tokens).toString();
      return {time: t.time, open: p, high: p, low: p, close: p, volumeWei: t.ethWei, trades: 1};
    })
    .filter((c) => c.close !== '0');
}

export function candleChart(candles: Candle[], currentPrice: bigint, trades: Trade[] = []): string {
  // A token whose whole history fits in one bucket still has a shape worth
  // drawing, so fall back to the trades rather than saying there is nothing.
  const series = candles.length >= 2 ? candles : tradesAsSeries(trades);
  if (series.length < 2) {
    return html`<div class="chart-empty">
      <span>Not enough trading yet to draw a chart.</span>
      <b>${formatEth(currentPrice, {places: 12})} ETH</b>
    </div>`;
  }

  const W = 720;
  const H = 220;
  const PAD = 8;

  const first = series[0]!;
  const last = series[series.length - 1]!;
  const t0 = first.time;
  const t1 = Math.max(last.time + 1, t0 + 1);

  let lo = Infinity;
  let hi = -Infinity;
  for (const c of series) {
    lo = Math.min(lo, Number(c.low));
    hi = Math.max(hi, Number(c.high));
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= 0) {
    return html`<div class="chart-empty"><span>No priced trades yet.</span></div>`;
  }
  // A flat series would collapse to a single line at the top of the box.
  if (hi === lo) {
    hi = lo * 1.05;
    lo = lo * 0.95;
  }
  const span = hi - lo;
  const x = (t: number) => PAD + ((t - t0) / (t1 - t0)) * (W - PAD * 2);
  const y = (v: number) => PAD + (1 - (v - lo) / span) * (H - PAD * 2);

  const points = series.map((c) => `${x(c.time).toFixed(1)} ${y(Number(c.close)).toFixed(1)}`);
  const line = `M${points.join(' L')}`;
  const area = `${line} L${x(last.time).toFixed(1)} ${H - PAD} L${x(t0).toFixed(1)} ${H - PAD} Z`;
  const rising = Number(last.close) >= Number(first.open);
  const stroke = rising ? '#69ce98' : '#d44b55';

  // Labels are HTML, not SVG text: the plot is stretched to the box with
  // `preserveAspectRatio="none"`, which would stretch glyphs with it.
  const ticks = [hi, lo + span / 2, lo].map(
    (v) => html`<span class="axis-tick" style="top:${((y(v) / H) * 100).toFixed(2)}%">${formatEth(
      BigInt(Math.round(v)),
      {places: 11},
    )}</span>`,
  );

  return html`<div class="chart" aria-label="Price over the last 24 hours">
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img">
      <title>Price over the last 24 hours, in ETH per token</title>
      <defs>
        <linearGradient id="fade" x1="0" y1="0" x2="0" y2="1">
          <stop stop-color="${stroke}" stop-opacity=".20"/>
          <stop offset="1" stop-color="${stroke}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${area}" fill="url(#fade)"/>
      <path d="${line}" fill="none" stroke="${stroke}" stroke-width="2" vector-effect="non-scaling-stroke"/>
    </svg>
    <div class="chart-axis" aria-hidden="true">${raw(ticks.join(''))}</div>
  </div>
  <div class="chart-caption">
    <span>24 hours ago</span>
    <span>${series.reduce((n, c) => n + c.trades, 0)} trades</span>
    <span>now</span>
  </div>`;
}
